import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Typography,
  Box,
  FormControl,
  FormHelperText,
  Button,
  Sheet,
  Select,
  Option,
  Checkbox,
  styled,
  useFinanceableTheme,
} from "@financeable-com-au/financeable-ui";
import {
  ApplicationFormSlice,
  Customer,
  ProductsQuote,
  UserDetailsState,
} from "@/store/slices/types/applicationFormSlice";
import { ERRORS_MESSAGE, SUCCESS_MESSAGE, TOOLTIP_MESSAGES } from "@/constants";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { IDispatchResponse } from "@/utils/interface";
import {
  sendProductQuotesToCustomers,
  userDetailsSelector,
  // @ts-expect-error - applicationFormSlice is .js, no types (suppresses following line)
} from "@/store/slices/applicationFormSlice";
import InfoTooltip from "@/components/InfoTooltip";

// The design-system `Select` is typed for single selection; this cast exposes the
// underlying Joy multi-select props (which the wrapper forwards at runtime).
const MultiSelect = Select as unknown as React.FC<{
  multiple: true;
  value: string[];
  onChange: (event: React.SyntheticEvent | null, value: string[] | null) => void;
  disabled?: boolean;
  placeholder?: string;
  intent?: "danger" | "warning";
  renderValue?: (selected: { value: string }[]) => React.ReactNode;
  slotProps?: { listbox?: Record<string, unknown> };
  children?: React.ReactNode;
}>;

// Uppercase field label — replaces the repeated inline label styling.
const SelectFieldLabel = styled(Typography)(({ theme }) => ({
  display: "block",
  marginBottom: "4px",
  color: theme.palette.neutral[500],
  fontWeight: 700,
  fontSize: "0.7rem",
  textTransform: "uppercase",
}));

// Send button with the brand gradient and disabled treatment baked in, so the
// gradient/shadow styling lives in one place instead of an inline `style` block.
const SendQuotesButton = styled(Button)(({ theme, disabled }) => ({
  minHeight: "52px",
  padding: "0 20px",
  borderRadius: "12px",
  fontSize: "0.9rem",
  fontWeight: 600,
  textTransform: "none",
  whiteSpace: "nowrap",
  color: theme.palette.common.white,
  background: disabled
    ? theme.palette.primary[200]
    : `linear-gradient(135deg, ${theme.palette.primary[400]} 0%, ${theme.palette.primary[600]} 100%)`,
  boxShadow: disabled ? "none" : "0 4px 12px rgba(96, 10, 221, 0.2)",
  opacity: disabled ? 0.5 : 1,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
}));

interface SendProductQuotesBarProps {
  productsQuotes: ProductsQuote[];
  customers: Customer[];
  isDisableApplicationEdit?: boolean;
}

const SendProductQuotesBar = React.memo(
  ({ productsQuotes, customers, isDisableApplicationEdit = false }: SendProductQuotesBarProps) => {
    const dispatch = useDispatch();
    const theme = useFinanceableTheme();
    const {
      application: { _id: applicationId },
      sendProductQuotesToCustomersLoader,
    } = useSelector<ApplicationFormSlice, UserDetailsState>(userDetailsSelector);
    const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);

    const customersWithEmail = customers.filter((customer) => customer.emails.length > 0);
    const quoteIds = productsQuotes.map((pQuote) => pQuote._id);
    const isSendQuotesDisabled =
      quoteIds.length === 0 ||
      selectedCustomers.length === 0 ||
      sendProductQuotesToCustomersLoader ||
      isDisableApplicationEdit;
    const hasNoCustomerEmail = customersWithEmail.length <= 0;

    const handle = {
      customerChange: (_event: React.SyntheticEvent | null, value: string[] | null) => {
        setSelectedCustomers(value ?? []);
      },
      sendQuotes: () => {
        if (isSendQuotesDisabled) return;

        dispatch(
          sendProductQuotesToCustomers({ productsQuotes: quoteIds, customers: selectedCustomers, applicationId }),
        ).then((res: IDispatchResponse) => {
          if (res?.error) {
            dispatch(
              openSnackbar({ message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg, intent: "danger" }),
            );
          } else {
            dispatch(
              openSnackbar({
                message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.productQuoteSentToCustomers,
                intent: "success",
              }),
            );
          }
        });
      },
    };

    let sendQuoteTooltipInfoText = "";

    if (quoteIds.length <= 0) {
      sendQuoteTooltipInfoText = TOOLTIP_MESSAGES.SEND_QUOTES_SELECT_QUOTE;
    } else if (hasNoCustomerEmail) {
      sendQuoteTooltipInfoText = TOOLTIP_MESSAGES.NO_CUSTOMER_EMAIL_FOR_SEND_QUOTE;
    } else if (selectedCustomers.length <= 0) {
      sendQuoteTooltipInfoText = TOOLTIP_MESSAGES.SEND_QUOTES_SELECT_CUSTOMER;
    }

    return (
      <Sheet
        variant="outlined"
        borderRadius="xl"
        sx={{
          p: 3,
          border: "2px solid rgba(96, 10, 221, 0.15)",
          background: theme.palette.common.white,
          boxShadow: "0 8px 32px rgba(96, 10, 221, 0.12)",
        }}
      >
        <FormControl error={hasNoCustomerEmail}>
          <SelectFieldLabel level="body-xs">Select Customers 👥</SelectFieldLabel>
          {/* Select and button share a flex row aligned to the top so they stay level
              regardless of the helper text below (which otherwise grew the left column
              and pushed the button out of alignment). */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
            <Box
              sx={{
                minWidth: 320,
                flex: 1,
                "& .MuiSelect-root": {
                  minHeight: "52px",
                  paddingInline: "16px",
                  borderRadius: "12px",
                  borderWidth: "2px",
                  fontSize: "0.95rem",
                  borderColor: hasNoCustomerEmail ? theme.palette.danger[300] : "rgba(96, 10, 221, 0.2)",
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    borderColor: hasNoCustomerEmail ? theme.palette.danger[400] : theme.palette.primary[300],
                  },
                  "&.Mui-focusVisible, &.Mui-expanded": {
                    borderColor: theme.palette.primary[400],
                    boxShadow: `0 0 0 3px ${theme.palette.primary[100]}`,
                  },
                },
              }}
            >
              <MultiSelect
                multiple
                value={selectedCustomers}
                onChange={handle.customerChange}
                disabled={isDisableApplicationEdit || hasNoCustomerEmail}
                placeholder="Choose customers..."
                intent={hasNoCustomerEmail ? "danger" : undefined}
                slotProps={{
                  listbox: {
                    // Left-align to the trigger. Joy defaults to `placement: "bottom"`
                    // (centered) plus an `equalWidth` modifier that resizes the listbox
                    // after it opens, which makes it jump horizontally on open / selection.
                    placement: "bottom-start",
                    sx: {
                      borderRadius: "12px",
                      padding: "6px",
                      maxHeight: "320px",
                      zIndex: 1300,
                      "& .MuiOption-root": {
                        borderRadius: "8px",
                        gap: "8px",
                        minHeight: "40px",
                      },
                    },
                  },
                }}
                renderValue={(selected: { value: string }[]) => {
                  if (selected.length === 0) return "Choose customers...";
                  return selected
                    .map(({ value: id }) => {
                      const customer = customersWithEmail.find((c) => String(c._id) === id);
                      return customer && customer.firstName
                        ? customer.firstName + " " + (customer.lastName || "")
                        : "(Unknown)";
                    })
                    .join(", ");
                }}
              >
                {customersWithEmail.map((customer) => (
                  <Option key={customer._id} value={String(customer._id)}>
                    <Checkbox checked={selectedCustomers.includes(String(customer._id))} readOnly size="sm" />
                    <Typography bold>{(customer.firstName || "unknown") + " " + (customer.lastName || "")}</Typography>
                  </Option>
                ))}
              </MultiSelect>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <SendQuotesButton variant="primary" onClick={handle.sendQuotes} disabled={isSendQuotesDisabled}>
                {sendProductQuotesToCustomersLoader ? "Sending ... 🚀" : "Send Quotes 🚀"}
              </SendQuotesButton>
              {sendQuoteTooltipInfoText && (
                <InfoTooltip title={sendQuoteTooltipInfoText} iconColor={theme.palette.primary[200]} />
              )}
            </Box>
          </Box>
          {hasNoCustomerEmail && <FormHelperText>{TOOLTIP_MESSAGES.NO_CUSTOMER_EMAIL_FOR_SEND_QUOTE}</FormHelperText>}
        </FormControl>
      </Sheet>
    );
  },
);

export { SendProductQuotesBar };
export default SendProductQuotesBar;
